import React, { useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import { useHistory } from "react-router";
import ServiceWrapper from "../../components/ServiceWrapper";
import { api } from "../../utils/api";
import { track } from "../../utils/track";
import { C, Page, Lead, Primary, Field, Err, Note, errText } from "./styles";
import { IQuota } from "./List";

/** 음원 길이(초)를 브라우저에서 잰다. 못 재면 0 (서버·PC 가 다시 확인) */
const measure = (file: File) =>
  new Promise<number>((resolve) => {
    const url = URL.createObjectURL(file);
    const a = new Audio();
    const done = (v: number) => { URL.revokeObjectURL(url); resolve(v); };
    a.preload = "metadata";
    a.onloadedmetadata = () => done(isFinite(a.duration) ? Math.round(a.duration) : 0);
    a.onerror = () => done(0);
    setTimeout(() => done(0), 8000);
    a.src = url;
  });

const fmt = (s: number) => `${Math.floor(s / 60)}분 ${s % 60}초`;

/** 새 악보 만들기 (/my-scores/new): 음원 파일 또는 음원 주소(유튜브 등) + 제목(+ 가수) → POST /my-scores
 *  가사 칸은 없앰 (2026-10-09 사장님): AI 가 노래를 받아 적고 다듬어서 넣음 */
const MyScoreNew: React.FC = () => {
  const history = useHistory();
  const fileRef = useRef<HTMLInputElement>(null);
  const [quota, setQuota] = useState<IQuota | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [sec, setSec] = useState(0);
  const [title, setTitle] = useState("");
  const [singer, setSinger] = useState("");
  const [mode, setMode] = useState<"file" | "url">("file");
  const [url, setUrl] = useState("");
  const [progress, setProgress] = useState<number | null>(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/my-scores/quota").then(({ data }) => setQuota(data.data)).catch(() => null);
  }, []);

  const pick = async (f: File | undefined) => {
    setErr("");
    if (!f) return;
    if (quota && f.size > quota.max_mb * 1024 * 1024) {
      setFile(null);
      setErr(`파일이 너무 커요(${Math.round(f.size / 1024 / 1024)}MB). ${quota.max_mb}MB 이하로 올려 주세요. 휴대폰 녹음 앱에서 mp3·m4a 로 저장하면 작아져요.`);
      return;
    }
    const s = await measure(f);
    if (quota && s > quota.max_sec + 5) {
      setFile(null);
      setErr(`곡이 너무 길어요(${fmt(s)}). ${Math.floor(quota.max_sec / 60)}분까지 가능해요.`);
      return;
    }
    setFile(f);
    setSec(s);
    if (!title) setTitle(f.name.replace(/\.[^.]+$/, "").slice(0, 100));
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ready || progress !== null) return;
    setErr("");
    setProgress(0);
    track(mode === "url" ? "myscore:url" : "myscore:upload");
    const form = new FormData();
    if (mode === "url") form.append("source_url", url.trim());
    else form.append("audio", file!);
    form.append("title", title.trim());
    form.append("singer", singer.trim());
    form.append("audio_sec", String(mode === "url" ? 0 : sec));
    try {
      const { data } = await api.post("/my-scores", form, {
        timeout: 10 * 60 * 1000,
        onUploadProgress: (p: ProgressEvent) => p.total && setProgress(Math.round((p.loaded / p.total) * 100)),
      });
      history.replace(`/my-scores/${data.data.id}`);
    } catch (e) {
      setProgress(null);
      setErr(errText(e, "올리지 못했어요. 인터넷 연결을 확인하고 다시 시도해 주세요."));
    }
  };

  const noLeft = !!quota && quota.left <= 0;
  const urlOk = /^https?:\/\/\S+\.\S+/i.test(url.trim());
  const ready = !!title.trim() && (mode === "url" ? urlOk : !!file);

  return (
    <ServiceWrapper isFooterRendered={false}>
      <Page>
        <h1>새 악보 만들기</h1>
        <Lead>녹음한 파일을 올리거나 유튜브 같은 음원 주소를 붙여 주세요. 코드·박자·가사를 AI 가 듣고 악보로 만들어요.</Lead>
        {noLeft ? (
          <Err>
            {quota!.plan === "free" ? "무료 체험을 이미 쓰셨어요. 구독하시면 매달 더 만들 수 있어요." : "이번 달에 만들 수 있는 곡을 모두 쓰셨어요. 다음 달 1일에 다시 채워져요."}
          </Err>
        ) : (
          <Form onSubmit={submit}>
            <Tabs role="tablist" aria-label="음원 넣는 방법">
              <button type="button" role="tab" aria-selected={mode === "file"} onClick={() => setMode("file")}>파일 올리기</button>
              <button type="button" role="tab" aria-selected={mode === "url"} onClick={() => setMode("url")}>주소로 넣기</button>
            </Tabs>
            {mode === "file" && (<>
            <input
              ref={fileRef}
              type="file"
              accept="audio/*,.mp3,.m4a,.wav,.aac"
              style={{ display: "none" }}
              onChange={(e) => pick(e.target.files ? e.target.files[0] : undefined)}
            />
            <Drop type="button" onClick={() => fileRef.current && fileRef.current.click()} has={!!file}>
              {file ? (
                <>
                  <b>{file.name}</b>
                  <span>{[sec ? fmt(sec) : "", `${(file.size / 1024 / 1024).toFixed(1)}MB`].filter(Boolean).join(" · ")} · 눌러서 바꾸기</span>
                </>
              ) : (
                <>
                  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 18V5l12-2v13M9 18a3 3 0 1 1-6 0 3 3 0 0 1 6 0zM21 16a3 3 0 1 1-6 0 3 3 0 0 1 6 0z" /></svg>
                  <b>음원 파일 고르기</b>
                  <span>mp3 · m4a · wav{quota ? ` · ${quota.max_mb}MB, ${Math.floor(quota.max_sec / 60)}분까지` : ""}</span>
                </>
              )}
            </Drop>
            </>)}
            {mode === "url" && (
              <Field>
                <span>음원 주소</span>
                <input
                  type="url"
                  inputMode="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://youtu.be/…"
                  autoComplete="off"
                />
                {url.trim() && !urlOk && <em style={{ color: C.err, fontStyle: "normal", fontSize: 13 }}>https:// 로 시작하는 주소를 붙여 주세요.</em>}
              </Field>
            )}
            <Field>
              <span>곡 제목</span>
              <input value={title} maxLength={100} onChange={(e) => setTitle(e.target.value)} placeholder="예: 우리 밴드 자작곡" />
            </Field>
            <Field>
              <span>가수·연주자 <em>(선택)</em></span>
              <input value={singer} maxLength={100} onChange={(e) => setSinger(e.target.value)} />
            </Field>
            <Primary type="submit" disabled={!ready || progress !== null}>
              {progress === null ? "악보 만들기" : progress < 100 ? `올리는 중… ${progress}%` : "거의 다 됐어요…"}
            </Primary>
            {err && <Err>{err}</Err>}
            <Note>
              {quota && (quota.plan === "free" ? "무료 체험 1곡을 써요. " : `이번 달 ${quota.left}곡 남았어요. `)}
              보통 10분 안에 완성되고, 다 되면 알림으로 알려 드려요. 가사는 AI 가 받아 적어 넣어요(노래 앞뒤 멘트는 빼요). 올린 음원은 악보를 만든 뒤 바로 지워요.
              <br />
              다른 사람의 음원을 허락 없이 올리지 말아 주세요.
            </Note>
          </Form>
        )}
        {err && noLeft && <Err>{err}</Err>}
      </Page>
    </ServiceWrapper>
  );
};

export default MyScoreNew;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Drop = styled.button<{ has: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 130px;
  padding: 16px;
  border-radius: 14px;
  border: 1.5px dashed ${({ has }) => (has ? C.accent : C.line)};
  background: ${({ has }) => (has ? "rgba(91,140,255,.08)" : C.card)};
  color: ${C.fg};
  cursor: pointer;
  text-align: center;
  & > svg {
    width: 30px;
    height: 30px;
    fill: none;
    stroke: ${C.accent};
    stroke-width: 1.8;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
  & > b {
    font-size: 16px;
    word-break: break-all;
  }
  & > span {
    font-size: 13px;
    color: ${C.muted};
  }
`;

const Tabs = styled.div`
  display: flex;
  gap: 4px;
  padding: 4px;
  border-radius: 12px;
  background: ${C.card};
  & > button {
    flex: 1;
    height: 40px;
    border: 0;
    border-radius: 9px;
    background: none;
    color: ${C.muted};
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
  }
  & > button[aria-selected="true"] {
    background: ${C.accent};
    color: #fff;
  }
`;
